import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import {
  ContentCopy,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const CodeBlock = styled(Paper)(({ theme }) => ({
  background: '#1e1e1e',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '4px',
  padding: theme.spacing(3),
  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
  fontSize: '0.875rem',
  lineHeight: 1.5,
  color: '#d4d4d4',
  whiteSpace: 'pre-wrap',
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#2d2d2d',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#555',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#777',
  },
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const CopyButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: '4px 8px',
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.7)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '4px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
  },
}));

const ExpandButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: '4px 8px',
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.7)',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
  },
}));

const prompts = [
  {
    id: 1,
    title: "E-commerce Weekly Analysis",
    description: "Comprehensive analysis of campaigns, products, placements & optimization insights",
    prompt: `You are a senior Google Ads analyst specializing in e-commerce Google Ads.
Account: [ACCOUNT_ID]
Date Range: [Last 7 days]

Please perform the following analyses:

1. **Campaign Overview**
   Summarize total impressions, clicks, spend, conversions, conversion value, ROAS, CTR, CVR, and CPA.

2. **Product-Level Performance**
   Break down performance metrics (impressions, clicks, spend, conversions, ROAS) by product or product group.
   Identify the top 10 best-performing and bottom 10 worst-performing products by ROAS and conversion volume.
   Flag products with week-over-week changes in ROAS or conversions exceeding ±15%.

3. **Placement Analysis**
   Analyze spend and conversion performance across key placements (Shopping, YouTube, Display, Discover, Gmail).
   Highlight which placements drive the highest ROAS and which are underperforming.

4. **Audience & Asset Insights**
   Review the audience segments contributing most to conversions and revenue.
   Identify any underperforming asset groups or creatives causing wasted spend or low CTR.

5. **Spend & Budget Efficiency**
   Check for pacing issues, budget caps, or inefficient spend allocation within campaigns.

6. **Root Cause Diagnostics**
   For flagged products or placements, diagnose potential issues such as feed data quality, auction competition, or creative fatigue.

7. **Recommendations**
   Prioritize 3–5 actionable recommendations for bidding, budget allocation, feed improvements, or creative refreshes.

8. **Output Format**
   Provide a structured Markdown report with:
   → An executive summary highlighting key metrics and notable changes.
   → Tables for product-level and placement performance (limit to 20 rows).
   → Bullet-pointed insights and prioritized next steps.
   Use human-friendly metrics (e.g., dollars, percentages) and clear headings.`
  },
  {
    id: 2,
    title: "Shopping Campaign Auction Insights",
    description: "Maximize performance via auction insights and impression share analysis",
    prompt: `You are a senior Google Ads analyst with expertise in Shopping campaigns.

For the Account: [ACCOUNT_ID], analyze auction insights and impression share data for the active Shopping campaigns over the period [DATE_RANGE].

Please perform the following:

1. **Impression Share Analysis**
   For each campaign, ad group, and product group, report:
   → Impression Share (IS)
   → Lost Impression Share due to Budget (IS Lost Budget %)
   → Lost Impression Share due to Rank (IS Lost Rank %)
   → Average Position or Top of Page Rate, if available

2. **Opportunity Identification**
   Identify where impression share loss is most significant (>10%) and determine whether it is primarily due to budget constraints or rank issues.

3. **Competitive Analysis**
   Detect any notable week-over-week changes in impression share metrics that may indicate competitive pressure or new entrants in auctions.

4. **Growth Opportunities**
   Provide insights on which campaigns or product groups have the highest opportunity for growth if the budget is increased or bids are improved.

5. **Actionable Recommendations**
   Recommend prioritized actions, such as:
   → Increasing budgets to recover lost impression share due to budget caps
   → Raising bids or improving quality signals for segments losing impression share due to rank
   → Restructuring campaigns or product groups to better compete in auctions

**Output:** A clear, concise Markdown report including:
→ Tables summarizing key impression share metrics at the campaign, ad group, and product group levels
→ A bullet-point summary of major findings and root causes
→ A prioritized action plan based on potential revenue impact

Present all data using clear visualizations with minimal technical jargon, focusing on business impact rather than ad metrics. Format as a visual dashboard with clear section dividers.`
  },
  {
    id: 3,
    title: "Keyword Optimization Analysis",
    description: "Optimize underperforming and identify high-potential keywords",
    prompt: `You are a senior Google Ads analyst focused on e-commerce Search campaigns.

Account: [ACCOUNT_ID]
Date Range: [SPECIFY_DATE_RANGE]

Analyze all active Search campaigns and provide only actionable insights by:

1. **Performance Criteria Analysis**
   Reporting on keywords and search queries that meet these criteria:
   - Keywords or queries with spend ≥ $X and ROAS below account average × 0.7 (potential waste)
   - Keywords or queries with ≥ 2 conversions and ROAS ≥ account average × 1.3 (growth opportunities)
   - Keywords or queries with week-over-week CTR or CVR declines > 15% (need ad or landing page optimization)
   - Queries generating spend with zero conversions (negative keyword candidates)

2. **Optimization Recommendations**
   For all items above, recommend specific optimizations, such as:
   - Bid increases or decreases
   - Adding new exact or phrase match keywords
   - Adding negative keywords
   - Ad copy or landing page improvements

3. **Focus on Actionable Items**
   Skip any keywords or queries that show stable or good performance and require no action.

4. **Structured Output**
   Provide a concise Markdown report including:
   - Summary tables limited to actionable items only
   - Bullet-pointed, prioritized recommendations
   - Clear section headers and human-readable metrics

Focus solely on what can be improved or optimized.`
  },
  {
    id: 4,
    title: "Account Segmentation Analysis",
    description: "Analyze and restructure account segmentation for better performance",
    prompt: `You are a senior Google Ads expert. Help me analyze the account.

Account: [ACCOUNT_ID]
Date Range: [SPECIFY_DATE_RANGE]

Please follow these steps:

1. **Segmentation Audit**
   Identify overly broad product groups that may be masking performance details.

2. **Granularity Evaluation**
   Evaluate the granularity and logic of the current segmentation.

3. **Restructuring Strategy**
   Suggest restructuring approaches to improve bid control and reporting clarity.

4. **Output Format**
   Return a Markdown report with segmentation insights and actionable restructuring advice.

Focus on practical recommendations that will improve campaign management efficiency and performance visibility.`
  },
  {
    id: 5,
    title: "Auction Insights & Competitive Analysis",
    description: "Analyze impression share losses and competitive changes",
    prompt: `Act as a Google Ads expert and help me analyze Account: [ACCOUNT_ID]

Review the auction insights for Search campaigns for Date Range: [SPECIFY_DATE_RANGE]

Please follow these steps:

1. **Impression Share Analysis**
   Report impression share, lost impression share due to budget, and lost impression share due to rank at both campaign and ad group levels.

2. **Competitive Intelligence**
   Detect significant changes indicating competitive pressures.

3. **Recovery Strategy**
   Recommend budget or bid changes to recover impression share.

4. **Structured Report**
   Deliver a Markdown report with data tables, insights, and prioritized recommendations.

Focus on actionable insights that can immediately impact impression share recovery and competitive positioning.`
  }
];

const PromptsPage: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<number[]>([]);
  const [copiedPrompts, setCopiedPrompts] = useState<number[]>([]);

  const toggleSection = (id: number) => {
    setExpandedSections(prev =>
      prev.includes(id)
        ? prev.filter(sectionId => sectionId !== id)
        : [...prev, id]
    );
  };

  const copyPrompt = async (prompt: string, id: number) => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedPrompts(prev => [...prev, id]);
      setTimeout(() => {
        setCopiedPrompts(prev => prev.filter(promptId => promptId !== id));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h2"
          component="h1"
          sx={{
            color: '#ffffff',
            fontWeight: 700,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            mb: 1,
          }}
        >
          Google Ads
        </Typography>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontWeight: 300,
            mb: 4,
          }}
        >
          Detailed Analyses - Prompts
        </Typography>
      </Box>

      {/* Prompts Sections */}
      <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
        {prompts.map((prompt, index) => (
          <Box key={prompt.id} sx={{ mb: 4 }}>
            <SectionHeader variant="h5">
              Prompt {index + 1}: {prompt.title}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ExpandButton
                  onClick={() => toggleSection(prompt.id)}
                  endIcon={expandedSections.includes(prompt.id) ? <ExpandLess /> : <ExpandMore />}
                >
                  {expandedSections.includes(prompt.id) ? 'Hide' : 'Show'}
                </ExpandButton>
                <CopyButton
                  startIcon={<ContentCopy />}
                  onClick={() => copyPrompt(prompt.prompt, prompt.id)}
                >
                  {copiedPrompts.includes(prompt.id) ? 'Copied!' : 'Copy'}
                </CopyButton>
              </Box>
            </SectionHeader>

            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                mb: 2,
                fontSize: '0.9rem',
              }}
            >
              {prompt.description}
            </Typography>

            {expandedSections.includes(prompt.id) && (
              <CodeBlock elevation={0}>
                {prompt.prompt}
              </CodeBlock>
            )}

            {index < prompts.length - 1 && (
              <Divider sx={{ mt: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', mt: 6, pt: 4 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '0.8rem',
          }}
        >
          Replace [ACCOUNT_ID] and [DATE_RANGE] placeholders with your specific values when using these prompts.
        </Typography>
      </Box>
      </Container>
    </Box>
  );
};

export default PromptsPage;
